import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { eventColors } from 'utils/dateCalculations';
import { isNotEmpty } from 'utils/type-guards';
import { uniqueByUuid } from 'utils/uniquByUuid';

export const DashboardEventsQueryFragmentDocument = graphql(`
  fragment DashboardEventsQueryFields on Query {
    calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: $calendarEventWhere) {
      __typename
      uuid
      duration
      start
      locations {
        uuid
      }
      virtualLocations {
        uuid
        token
      }
      classShortNames
      categories
      title
      description
      keywords
      holderConnection {
        edges {
          node {
            __typename
            ... on Group {
              uuid
              displayName
            }
            ... on Organization {
              uuid
              displayName
            }
            ... on Profile {
              uuid
              displayName
            }
          }
        }
      }
    }
    birthdayEvents {
      uuid
      duration
      title
      start
      color
      categories
      classShortNames
    }
  }
`);

export function getDashboardEvents(query: FragmentType<typeof DashboardEventsQueryFragmentDocument> | undefined) {
  const fragment = getFragmentData(DashboardEventsQueryFragmentDocument, query);
  const birthdayEvents = fragment?.birthdayEvents;
  const calendarEvents = fragment?.calendarEvents;
  const birthdays =
    birthdayEvents
      ?.map((bE) => ({
        attendees: [],
        categories: bE.categories ?? [],
        classShortNames: bE.classShortNames,
        color: eventColors(bE.categories ?? [], true),
        description: undefined,
        duration: bE.duration,
        guestsAllowed: false,
        keywords: undefined,
        locations: [],
        locationsCount: 0,
        originDisplayName: undefined,
        owner: false,
        start: dayjs(bE.start),
        title: bE.title ?? '',
        type: 'birthday',
        uuid: bE.uuid,
        virtualLocations: [],
        virtualLocationsCount: 0,
      }))
      .filter(isNotEmpty) ?? [];

  const events = (calendarEvents ?? [])?.map((e) => {
    const eventColor = eventColors(e.categories ?? []);

    const holderNode = e.holderConnection.edges.find((h) => (h.node.uuid ?? '').length > 0)?.node;

    return {
      categories: e.categories ?? [],
      classShortNames: e.classShortNames,
      color: eventColor,
      description: e.description ?? '',
      duration: e.duration ?? undefined,
      keywords: e.keywords ?? [],
      locationsCount: e.locations.length,
      originDisplayName: holderNode?.displayName ?? '',
      start: dayjs(e.start),
      title: e.title ?? '',
      type: 'event',
      uuid: e.uuid,
      virtualLocationsCount: e.virtualLocations.length,
    };
  });

  return uniqueByUuid(
    [...events, ...birthdays].sort((a, b) => {
      const startA = new Date(a.start.toDate()).getTime();
      const startB = new Date(b.start.toDate()).getTime();
      return startA - startB;
    }),
  );
}

export type DashboardEvent = ReturnType<typeof getDashboardEvents>[0];
